// Mileage periods, as the app's MileagePeriod: calendar spans in local time, end exclusive.

export type PeriodKind = "all" | "thisMonth" | "lastMonth" | "lastQuarter" | "thisYear" | "lastYear" | "custom";

export interface Interval {
  start: Date;
  /** Exclusive: the midnight after the last day. */
  end: Date;
}

export interface Period {
  kind: PeriodKind;
  /** For custom periods. */
  interval?: Interval;
}

export const presetKinds: PeriodKind[] = ["thisMonth", "lastMonth", "lastQuarter", "thisYear", "lastYear"];

const titles: Record<PeriodKind, string> = {
  all: "All trips",
  thisMonth: "This Month",
  lastMonth: "Last Month",
  lastQuarter: "Last Quarter",
  thisYear: "This Year",
  lastYear: "Last Year",
  custom: "Custom",
};

function monthStart(year: number, month: number): Date {
  return new Date(year, month, 1);
}

/** The interval a period covers. "All" has none. */
export function periodInterval(period: Period, now: Date): Interval | undefined {
  const year = now.getFullYear();
  const month = now.getMonth();
  switch (period.kind) {
    case "all":
      return undefined;
    case "thisMonth":
      return { start: monthStart(year, month), end: monthStart(year, month + 1) };
    case "lastMonth":
      return { start: monthStart(year, month - 1), end: monthStart(year, month) };
    case "thisYear":
      return { start: new Date(year, 0, 1), end: new Date(year + 1, 0, 1) };
    case "lastYear":
      return { start: new Date(year - 1, 0, 1), end: new Date(year, 0, 1) };
    case "lastQuarter": {
      // The three calendar months before the quarter `now` is in.
      const quarterStart = month - (month % 3);
      return { start: monthStart(year, quarterStart - 3), end: monthStart(year, quarterStart) };
    }
    case "custom":
      return period.interval;
  }
}

/** A custom period covering two picked days in full, whichever order they were picked in. */
export function customPeriod(first: Date, second: Date): Period {
  const [a, b] = first <= second ? [first, second] : [second, first];
  const start = new Date(a.getFullYear(), a.getMonth(), a.getDate());
  const end = new Date(b.getFullYear(), b.getMonth(), b.getDate() + 1);
  return { kind: "custom", interval: { start, end } };
}

/** "Jan 1 – Oct 14, 2026" for an interval whose end is the exclusive midnight after its last day. */
export function rangeText(interval: Interval, locale?: string): string {
  const last = new Date(Math.max(interval.start.getTime(), interval.end.getTime() - 1));
  const format = new Intl.DateTimeFormat(locale, { month: "short", day: "numeric", year: "numeric" });
  return format.formatRange(interval.start, last);
}

/** The part of a period up to the end of today, for "Jan 1 – Oct 16, 2026" beside one still running. */
export function elapsedInterval(interval: Interval, now: Date): Interval {
  const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  const end = new Date(Math.max(interval.start.getTime(), Math.min(interval.end.getTime(), endOfToday.getTime())));
  return { start: interval.start, end };
}

export function periodTitle(period: Period, locale?: string): string {
  if (period.kind === "custom" && period.interval) return rangeText(period.interval, locale);
  return titles[period.kind];
}

export function contains(interval: Interval | undefined, date: Date): boolean {
  return !interval || (interval.start <= date && date < interval.end);
}

/** Whole days in an interval. */
export function dayCount(interval: Interval): number {
  let days = 0;
  const cursor = new Date(interval.start);
  while (cursor < interval.end && days <= 400) {
    cursor.setDate(cursor.getDate() + 1);
    days += 1;
  }
  return days;
}
